"use client";

import { usePathname, useSearchParams } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  bareListHref,
  type ListReturnPath,
  type ListReturnScope,
  matchListReturnPath,
  readListReturnHref,
  readSidebarListHref,
  saveListReturnHref,
  subscribeListReturn,
} from "./list-return";

const ListReturnContext = createContext<ListReturnScope | null>(null);

/** Gives list links the member and organization their stored URLs belong to. */
export function ListReturnProvider({
  children,
  orgId,
  orgSlug,
  userId,
}: ListReturnScope & { children: React.ReactNode }) {
  const scope = useMemo(
    () => ({ orgId, orgSlug, userId }),
    [orgId, orgSlug, userId]
  );
  return (
    <ListReturnContext.Provider value={scope}>
      {children}
    </ListReturnContext.Provider>
  );
}

export function useListReturnScope(): ListReturnScope | null {
  return useContext(ListReturnContext);
}

/**
 * Saves the URL of the list on screen. Filters write the URL from many
 * places, so one recorder in the dashboard layout covers every list.
 * Render it inside a `Suspense` boundary: it reads the search params.
 */
export function ListReturnRecorder() {
  const scope = useListReturnScope();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();

  useEffect(() => {
    if (!scope) {
      return;
    }
    const listPath = matchListReturnPath(pathname, scope.orgSlug);
    if (!listPath) {
      return;
    }
    saveListReturnHref(
      scope,
      listPath,
      search ? `${pathname}?${search}` : pathname
    );
  }, [scope, pathname, search]);

  return null;
}

/**
 * The URL a link to a list opens. The server and the first client render use
 * the bare `href`, so hydration matches; the stored URL follows after.
 * Hrefs that are not a known list of this organization stay as they are.
 */
export function useListReturnHref(
  href: string,
  variant: "back" | "sidebar" = "back"
): string {
  const scope = useListReturnScope();
  const listPath: ListReturnPath | null = scope
    ? matchListReturnPath(href, scope.orgSlug)
    : null;

  const getSnapshot = useCallback(() => {
    if (!(scope && listPath)) {
      return href;
    }
    return variant === "sidebar"
      ? readSidebarListHref(scope, listPath)
      : readListReturnHref(scope, listPath);
  }, [href, listPath, scope, variant]);
  const getServerSnapshot = useCallback(() => href, [href]);

  return useSyncExternalStore(
    subscribeListReturn,
    getSnapshot,
    getServerSnapshot
  );
}

/** Reads the sidebar URL of a list at event time, such as a shortcut. */
export function useSidebarListHrefReader(): (href: string) => string {
  const scope = useListReturnScope();
  return useCallback(
    (href: string) => {
      if (!scope) {
        return href;
      }
      const listPath = matchListReturnPath(href, scope.orgSlug);
      return listPath && href === bareListHref(scope.orgSlug, listPath)
        ? readSidebarListHref(scope, listPath)
        : href;
    },
    [scope]
  );
}
